// src/utils/errorMessages.js

export const ERROR_MESSAGES = {
  login: {
    "auth/invalid-email": {
      title: "Invalid Email",
      message:
        "That doesn’t look like a valid work email. Please check for spelling mistakes (e.g. missing @ symbol).",
    },
    "auth/user-not-found": {
      title: "Account Not Found",
      message:
        "We couldn’t find an Oiiko account with this email address. If you believe this is a mistake, please contact your employer or Oiiko Support.",
    },
    "auth/wrong-password": {
      title: "Incorrect Password",
      message:
        "The password you entered is incorrect. Try again or reset your password if you’ve forgotten it.",
    },
    "auth/invalid-credential": {
      title: "Incorrect Credentials",
      message:
        "Your email or password is incorrect. Try again or reset your password.",
    },
    "auth/too-many-requests": {
      title: "Too Many Attempts",
      message:
        "Your account has been temporarily locked due to repeated failed sign-in attempts. Please wait a few minutes or reset your password.",
    },
    "auth/network-request-failed": {
      title: "Network Error",
      message:
        "We couldn’t connect to Oiiko servers. Please check your internet connection and try again.",
    },

    default: {
      title: "Login Error",
      message:
        "We couldn’t sign you in right now. Please try again shortly or contact Oiiko Support.",
    },
  },

  resetPassword: {
    "auth/invalid-email": {
      title: "Invalid Email",
      message: "Please enter a valid email address before requesting a reset.",
    },
    "auth/user-not-found": {
      title: "Email Not Recognized",
      message:
        "If an account exists for this email, you will receive a reset link. Please check your inbox.",
    },
    "auth/too-many-requests": {
      title: "Too Many Attempts",
      message:
        "Please wait a few minutes before requesting another reset link.",
    },
    default: {
      title: "Reset Password Error",
      message:
        "We couldn’t send the reset email right now. Please try again in a few moments.",
    },
  },

  // When you send next files (Register.js, Leave, Scheduling, Time Tracking)
  // I’ll help you fill THEIR dictionaries too.

  // 🔹 NEW: Register page error messages
  register: {
    "auth/email-already-in-use": {
      title: "Email Already Registered",
      message:
        "This email is already associated with an Oiiko account. Try logging in instead or use ‘Forgot your password?’ to reset access.",
    },
    "auth/invalid-email": {
      title: "Invalid Email Address",
      message:
        "That doesn’t look like a valid company email. Please check for typos (e.g., missing @ symbol) and try again.",
    },
    "auth/weak-password": {
      title: "Password Too Weak",
      message:
        "Your password doesn’t meet our security requirements. Please choose a stronger password with a mix of letters, numbers, and symbols.",
    },
    "auth/network-request-failed": {
      title: "Network Error",
      message:
        "We couldn’t reach Oiiko servers to create your account. Please check your internet connection and try again.",
    },
    "auth/operation-not-allowed": {
      title: "Registration Not Available",
      message:
        "Email/password sign-up is currently disabled for this project. Please contact Oiiko Support or your administrator.",
    },

    default: {
      title: "Registration Error",
      message:
        "We couldn’t create your account right now. Please review your details and try again, or contact Oiiko Support if the issue continues.",
    },
  },

  // 🔹 NEW: Company setup page errors
  companySetup: {
    "permission-denied": {
      title: "Permission Error",
      message:
        "Your account doesn’t have permission to create a company. Please sign out and sign back in, or contact Oiiko Support.",
    },
    unavailable: {
      title: "Service Unavailable",
      message:
        "We’re having trouble connecting to the database. Please wait a moment and try again.",
    },
    "deadline-exceeded": {
      title: "Request Timed Out",
      message:
        "Company setup took longer than expected. Please try again; if this keeps happening, contact Oiiko Support.",
    },
    default: {
      title: "Company Setup Error",
      message:
        "We couldn’t complete your company setup right now. Please review your details and try again, or contact Oiiko Support if the issue continues.",
    },
  },

  // 🔹 NEW: Subscription / billing page errors
  subscription: {
    "subscription/no-stripe-customer": {
      title: "Billing Profile Missing",
      message:
        "We couldn’t find a Stripe billing profile for this account. Please contact Oiiko Support so we can finalize your billing setup.",
    },
    "permission-denied": {
      title: "No Permission to Manage Billing",
      message:
        "Your current account doesn’t have permission to manage this subscription. Try signing in as the owner or contact Oiiko Support.",
    },
    unavailable: {
      title: "Billing Service Unavailable",
      message:
        "Our billing service is temporarily unavailable. Please wait a few minutes and try again.",
    },
    "deadline-exceeded": {
      title: "Billing Request Timed Out",
      message:
        "The request to update your subscription took longer than expected. Please refresh the page and try again. You won’t be double-charged.",
    },
    "not-found": {
      title: "Subscription Not Found",
      message:
        "We couldn’t find an active subscription linked to this account. Please select a plan to get started, or contact Oiiko Support if this seems wrong.",
    },
    default: {
      title: "Subscription Error",
      message:
        "We couldn’t update your subscription right now. Please try again in a moment, and contact Oiiko Support if the issue continues.",
    },
  },
};

// -------- FORMATTER FUNCTION ----------
export function formatError(page, err) {
  const code = err?.code || "";
  const pageErrors = ERROR_MESSAGES[page];

  if (!pageErrors)
    return { title: "Error", message: "An unexpected error occurred." };

  if (pageErrors[code]) return pageErrors[code];

  return pageErrors.default;
}
